import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Show, SignIn, SignUp, useAuth } from "@clerk/react";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { Toaster } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/legal/CookieConsent";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AppShell from "@/components/app/AppShell";
import Dashboard from "@/pages/Dashboard";
import Integrations from "@/pages/Integrations";
import Billing from "@/pages/Billing";
import Emails from "@/pages/Emails";
import AdminUsage from "@/pages/AdminUsage";
import AdminSaveOffers from "@/pages/AdminSaveOffers";
import AdminCleanup from "@/pages/AdminCleanup";
import AdminGrowth from "@/pages/AdminGrowth";
import Feedback from "@/pages/Feedback";
import About from "@/pages/About";
import Security from "@/pages/Security";
import QuoteConfirm from "@/pages/QuoteConfirm";
import { HeyCatchIdentity } from "@/components/HeyCatchIdentity";
import { HEYCATCH_SPLIT, HEYCATCH_SURFACE, isAppPath } from "@/lib/heycatch";

// Website and app report to separate HeyCatch projects; crossing between them reloads once so the right one starts.
const SurfaceSwitch = () => {
  const { pathname } = useLocation();
  const { isSignedIn } = useAuth();
  useEffect(() => { if (!window.location.hash) window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => {
    if (!HEYCATCH_SPLIT || (pathname === "/" && isSignedIn)) return;
    if ((isAppPath(pathname) ? "app" : "landing") !== HEYCATCH_SURFACE) window.location.replace(window.location.href);
  }, [pathname, isSignedIn]);
  return null;
};
import AlertSettings from "@/pages/AlertSettings";
import PaymentResult from "@/pages/PaymentResult";
import Terms from "@/pages/Terms";
import Account from "@/pages/Account";
import Team from "@/pages/Team";
import Join from "@/pages/Join";

const Protected = ({ children }) => (
  <>
    <Show when="signed-in">{children}</Show>
    <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
  </>
);

const Home = ({ section }) => (
  <>
    <Show when="signed-in">{section ? <Landing section={section} /> : <Navigate to="/app" replace />}</Show>
    <Show when="signed-out"><Landing section={section} /></Show>
  </>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ClerkAuthProvider>
          <HeyCatchIdentity />
          <SurfaceSwitch />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/features" element={<Home section="features" />} />
            <Route path="/pricing" element={<Home section="pricing" />} />
            <Route path="/about" element={<About />} />
            <Route path="/security" element={<Security />} />
            <Route path="/quote-confirm" element={<QuoteConfirm />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/join" element={<Join />} />
            <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthPage>} />
            <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
            <Route path="/app" element={<Protected><AppShell /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="billing" element={<Billing />} />
              <Route path="emails" element={<Emails />} />
              <Route path="admin/usage" element={<AdminUsage />} />
              <Route path="admin/save-offers" element={<AdminSaveOffers />} />
              <Route path="admin/cleanup" element={<AdminCleanup />} />
              <Route path="admin/growth" element={<AdminGrowth />} />
              <Route path="feedback" element={<Feedback />} />
              <Route path="alerts" element={<AlertSettings />} />
              <Route path="account" element={<Account />} />
              <Route path="team" element={<Team />} />
            </Route>
            <Route path="/payment/:result" element={<Protected><PaymentResult /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster theme="dark" position="bottom-right" />
          <CookieConsent />
        </ClerkAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
